import * as React from 'react';
import { connect } from 'react-redux';

import ActionButton from 'components/buttons/action-button/ActionButton';
import InlineEditingWrapper from 'components/inline-editing-wrapper/InlineEditingWrapper';
import Input from 'components/inputs/input/Input';
import Spinner from 'components/spinner/Spinner';
import PromptExit from 'libs/router/prompt-exit/PromptExit';
import { ModalFrameFooter, ModalFrameMessage } from 'components/modal';
import BrandTabsModal from 'components/brands-filter/BrandTabsModal';
import withInfluencerMeta from 'components/brands-filter/WithInfluencerMeta';

import Note from '../note/Note';
import { addNote, deleteNote, fetchNotes, resetNotes, updateNote } from 'modules/notes/reducers/notes';

import { fetchInfluencersMeta } from 'modules/influencers/reducers/influencersMeta';
import { isRequired, maxLength } from 'utils/validators';
import { getPlainText, isEmptyEditorText } from 'utils/editor';

import { __t, applyTranslations } from 'utils/language';

import type {
  InfluencerMeta,
  InfluencerProfile,
  Note as NoteAlias,
  Notes
} from 'types';

import './notes-modal.scss';

applyTranslations(require('./notes-modal.i18n.json'));
applyTranslations(require('locales/common/collabary-common.i18n.json'));

const NOTE_ID_NOT_SELECTED = -1;
const NODE_ID_NEW = -99;
const NOTE_MAX_LENGTH = 1000;

type OwnProps = {
  influencerProfile: InfluencerProfile,
  influencerMeta: InfluencerMeta,
  onBeforeCloseModal: () => void
};

type StateProps = {
  currentAccountId: number | undefined | null,
  notes: Notes,
  activeBrandFilter: string
};

type DispatchProps = {
  dispatchFetchNotes: (influencerId: number, filter: string) => Promise<void>,
  dispatchResetNotes: () => Promise<void>,
  dispatchAddNote: (text: string, influencerId: number) => Promise<void>,
  dispatchDeleteNote: (b: number, a: NoteAlias) => Promise<void>,
  dispatchUpdateNote: (c: string, b: number, a: NoteAlias) => Promise<void>,
  dispatchUpdateInfluencerMeta: (a: number) => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  currentEditingNoteId: number
};

class NotesModal extends React.PureComponent<Props, State> {
  state: State = {
    currentEditingNoteId: NOTE_ID_NOT_SELECTED
  };

  componentDidMount(): void {
    this.loadNotes();
  }

  componentDidUpdate(prevProps: Props) {
    const { activeBrandFilter, notes } = this.props;
    const { currentEditingNoteId } = this.state;

    if (activeBrandFilter !== prevProps.activeBrandFilter) {
      this.loadNotes();
    }

    if (notes.creatorList !== prevProps.notes.creatorList && currentEditingNoteId > 0 && !notes.creatorList.find(n => n.id === currentEditingNoteId)) {
      this.handleExitEditMode();
    }
  }

  componentWillUnmount(): void {
    const { dispatchResetNotes } = this.props;

    dispatchResetNotes();
  }

  loadNotes(): void {
    const { activeBrandFilter, dispatchFetchNotes, influencerProfile } = this.props;

    dispatchFetchNotes(influencerProfile.id, activeBrandFilter);
  }

  updateInfluencerMeta = () => {
    const { influencerProfile, dispatchUpdateInfluencerMeta } = this.props;

    dispatchUpdateInfluencerMeta(influencerProfile.id);
  };

  isEditModeAllowedForNote(note: NoteAlias): boolean {
    const { currentAccountId } = this.props;
    const { currentEditingNoteId } = this.state;

    return (currentEditingNoteId === note.id || currentEditingNoteId === NOTE_ID_NOT_SELECTED) &&
      note.createdBy.id === currentAccountId;
  }

  handleAddNote = (text?: any): void => {
    const { dispatchAddNote, influencerProfile } = this.props;

    const trimmedText = text ? text.replace(/\n$/, '') : '';
    const hasEmptyNewNote = isEmptyEditorText(trimmedText);

    if (hasEmptyNewNote || !trimmedText) {
      this.handleExitEditMode();
      return;
    }

    dispatchAddNote(trimmedText, influencerProfile.id)
      .then(this.handleExitEditMode)
      .then(this.updateInfluencerMeta);
  };

  handleEditNote = (note: NoteAlias, text: string) => {
    const { dispatchUpdateNote, influencerProfile } = this.props;
    const trimmedText = text ? text.replace(/\n$/, '') : '';

    dispatchUpdateNote(trimmedText, influencerProfile.id, note)
      .then(this.handleExitEditMode);
  };

  handleExitEditMode = (): void => {
    this.setState({
      currentEditingNoteId: NOTE_ID_NOT_SELECTED
    });
  };

  handleDeleteNote = (note: NoteAlias): void => {
    const { dispatchDeleteNote, influencerProfile } = this.props;

    dispatchDeleteNote(influencerProfile.id, note)
      .then(this.updateInfluencerMeta);
  };

  handleNewNote = (): void => {
    this.setState({
      currentEditingNoteId: NODE_ID_NEW
    });
  };

  handleModified = (currentEditingNoteId: number): void => {
    this.setState({
      currentEditingNoteId
    });
  };

  renderNoteEdit = () => (
    <Input
      width="responsive"
      autoFocus
      multiline
      value="" // will be overwritten in InlineEditingWrapper
    />
  );

  renderAddNoteForm(): React.ReactNode {
    return (
      <InlineEditingWrapper
        inGlobalEditMode={false}
        onSubmit={this.handleAddNote}
        onCancel={this.handleAddNote}
        renderViewMode={this.renderNoteEdit}
        renderEditMode={this.renderNoteEdit}
        inForceEditMode
        initialValue=""
        validatorResolvers={[
          (value) => isRequired(value),
          (value) => maxLength(getPlainText(value), NOTE_MAX_LENGTH)
        ]}
      />
    );
  }

  render(): React.ReactNode {
    const { notes, influencerProfile, onBeforeCloseModal, influencerMeta } = this.props;
    const { currentEditingNoteId } = this.state;
    const hasNotes = Boolean(notes && notes.creatorList.length);

    return (
      <section>
        {
          currentEditingNoteId !== NOTE_ID_NOT_SELECTED &&
            <PromptExit type="modal" />
        }
        <BrandTabsModal
          title={__t('notes_modal.notes_title')}
          influencerProfile={influencerProfile}
          influencerMeta={influencerMeta}
          metaKey="notes"
        >
          {
            (notes.isLoading && !hasNotes) && (
              <Spinner
                position="inline"
                size="small"
                color="light"
              />
            )
          }
          <div className="notes-modal-content">
            <div className="notes-modal-content__actions">
              <ActionButton
                key="notes-modal__add-button"
                color="secondary"
                disabled={currentEditingNoteId !== NOTE_ID_NOT_SELECTED}
                onClick={this.handleNewNote}
              >
                {__t('notes_modal.add_button_label')}
              </ActionButton>
            </div>
            {
              currentEditingNoteId === NODE_ID_NEW &&
                this.renderAddNoteForm()
            }
            {
              hasNotes ? (
                <React.Fragment>
                  {
                    notes.creatorList.map((note) => (
                      <Note
                        key={note.id}
                        note={note}
                        isEditModeAllowed={this.isEditModeAllowedForNote(note)}
                        onCancel={this.handleExitEditMode}
                        onDelete={this.handleDeleteNote}
                        onEdit={this.handleEditNote}
                        onModified={this.handleModified}
                      />
                    ))
                  }
                </React.Fragment>
              ) : !notes.isLoading && currentEditingNoteId !== NODE_ID_NEW && (
                <ModalFrameMessage
                  message={__t('notes_modal.empty_messages')}
                />
              )
            }
          </div>
          <ModalFrameFooter>
            <ActionButton
              color="secondary"
              disabled={currentEditingNoteId !== NOTE_ID_NOT_SELECTED}
              onClick={onBeforeCloseModal}
            >
              {__t('collabary_common.action_button.done')}
            </ActionButton>
          </ModalFrameFooter>
        </BrandTabsModal>
      </section>
    );
  }
}

const mapStateToProps = ({ notes, brandsFilter, account }): StateProps => ({
  notes,
  activeBrandFilter: brandsFilter.activeFilter,
  currentAccountId: account.currentAccount ? account.currentAccount.id : null
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchNotes: (influencerId: number, activeBrandFilter: string) => dispatch(fetchNotes(influencerId, activeBrandFilter)),
  dispatchResetNotes: () => dispatch(resetNotes()),
  dispatchAddNote: (text: string, influencerId: number) => dispatch(addNote(text, influencerId)),
  dispatchDeleteNote: (influencerId: number, note) => dispatch(deleteNote(influencerId, note)),
  dispatchUpdateNote: (text, influencerId, note) => dispatch(updateNote(text, influencerId, note)),
  dispatchUpdateInfluencerMeta: (influencerId) => dispatch(fetchInfluencersMeta([influencerId]))
});

const EnhancedNotesModal = withInfluencerMeta()(NotesModal);
export default connect(mapStateToProps, mapDispatchToProps)(EnhancedNotesModal);
